import { memo, useState } from "react";
import { classNames, useTranslate } from "helpers";
import { ReactSVG } from "react-svg";
import { ContractBuyerCardProps } from "./types";
import ContactEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactEditCard/ContactEditCard";
import { getContactAPI, getContactRelationsAPI, getContactPhonesAPI } from "services/api/contacts/api";
import { initialDataWithPagination } from "services/api/consts";

const LESSEE_FIELDS = [
  "lesseeName",
  "lesseePersonalIdentificationNumber",
  "lesseeAddress",
  "lesseePhoneNumber",
  "lesseeMobilePhoneNumber",
  "lesseeEmail",
  "lesseeAuthorizedRepresentativeName",
] as const;

const InfoRow: React.FC<{ label: string; value: string | null; hasError?: boolean }> = ({ label, value, hasError }) => (
  <div className="flex justify-between py-1.5">
    <span className="text-sm text-slate-500">{label}</span>
    <span className={classNames(
      "text-sm font-medium",
      value ? "text-green-700" : hasError ? "text-red-500" : "text-slate-900"
    )}>{value || (hasError ? "Potrebno popuniti" : "—")}</span>
  </div>
);

const ContractBuyerCard: React.FC<ContractBuyerCardProps> = ({
  formValues,
  setFormValues,
  errors,
  setErrors,
}) => {

  const { t } = useTranslate();
  const [showEditModal, setShowEditModal] = useState(false);

  const phoneOrMobileFilled = !!formValues.lesseePhoneNumber || !!formValues.lesseeMobilePhoneNumber;

  const hasErrors = LESSEE_FIELDS.some(field => {
    if ((field === "lesseePhoneNumber" || field === "lesseeMobilePhoneNumber") && phoneOrMobileFilled) return false;
    return !!errors[field];
  });

  const handleOpenModal = () => {
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
  };

  const handleEditSuccess = async () => {
    setShowEditModal(false);

    if (!formValues.lesseeContactId) return;

    try {
      const contact = await getContactAPI(formValues.lesseeContactId);
      const isCompany = contact.contactType?.id === "C";

      const phone = contact.primaryContactPhone;
      const isMobile = phone?.phoneTypeId === "M";

      const address = contact.primaryContactAddress;
      const formattedAddress = address
        ? [
            address.line1,
            address.line2,
            [address.postalCode, address.city].filter(Boolean).join(" "),
            address.country,
          ].filter(Boolean).join(", ")
        : null;

      let representativeName: string | null = null;
      let representativeEmail: string | null = null;
      let representativeFixedPhone: string | null = null;
      let representativeMobilePhone: string | null = null;

      if (isCompany) {
        const relations = await getContactRelationsAPI(formValues.lesseeContactId);
        if (relations.length > 0) {
          const rep = relations[0];
          representativeName = `${rep.personContact?.firstName || ""} ${rep.personContact?.lastName || ""}`.trim() || null;
          representativeEmail = rep.personContact?.primaryContactEmail?.emailAddress || null;

          if (rep.personContact?.id) {
            const phones = await getContactPhonesAPI(initialDataWithPagination.pagination, rep.personContact.id);
            representativeFixedPhone = phones.data.find(p => p.phoneTypeId === "F")?.phoneNumber || null;
            representativeMobilePhone = phones.data.find(p => p.phoneTypeId === "M")?.phoneNumber || null;
          }
        }
      }

      setFormValues(prev => ({
        ...prev,
        lesseeName: contact.name || contact.companyName || `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || null,
        lesseePersonalIdentificationNumber: contact.personalIdentificationNumber || null,
        lesseeAddress: formattedAddress,
        lesseePhoneNumber: isCompany
          ? (representativeFixedPhone || (!isMobile ? phone?.phoneNumber : null) || null)
          : (!isMobile ? phone?.phoneNumber || null : null),
        lesseeMobilePhoneNumber: isCompany
          ? (representativeMobilePhone || (isMobile ? phone?.phoneNumber : null) || null)
          : (isMobile ? phone?.phoneNumber || null : null),
        lesseeEmail: isCompany
          ? (representativeEmail || contact.primaryContactEmail?.emailAddress || null)
          : (contact.primaryContactEmail?.emailAddress || null),
        lesseeAuthorizedRepresentativeName: isCompany
          ? representativeName
          : `${contact.firstName || ""} ${contact.lastName || ""}`.trim() || null,
        lesseeContactTypeId: contact.contactType?.id ?? null,
      }));

      setErrors(prev => {
        const next = { ...prev };
        LESSEE_FIELDS.forEach(field => delete next[field]);
        return next;
      });
    } catch {
      // error handled by snackbar
    }
  };

  return (
    <>
      <div className={classNames(
        "flex-1 bg-white rounded-sm border overflow",
        hasErrors ? "border-red-300" : "border-slate-300"
      )}>
        <div className={classNames(
          "flex justify-between items-center px-3.5 py-2.5",
          hasErrors ? "bg-red-50" : "bg-slate-50"
        )}>
          <div className="flex items-center gap-2">
            <span className="text-md font-medium text-slate-900">Najmoprimac</span>
            {hasErrors && (
              <span className="w-2 h-2 rounded-full bg-red-500" />
            )}
          </div>
          <button type="button" onClick={handleOpenModal} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-sky-500 bg-sky-50 text-sky-700 text-sm font-medium hover:bg-sky-100 transition-colors">
            <ReactSVG src="/media/icons/edit.svg" className="[&_svg]:w-3.5 [&_svg]:h-3.5 [&_path]:stroke-sky-600" />
            Uredi
          </button>
        </div>
        <div className={classNames("flex flex-col px-3.5 py-3", hasErrors && "bg-white")}>
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyername")} value={formValues.lesseeName} hasError={!!errors.lesseeName} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyerpersonalidentificationnumber")} value={formValues.lesseePersonalIdentificationNumber} hasError={!!errors.lesseePersonalIdentificationNumber} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyeraddress")} value={formValues.lesseeAddress} hasError={!!errors.lesseeAddress} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyerphonenumber")} value={formValues.lesseePhoneNumber} hasError={!formValues.lesseePhoneNumber && !formValues.lesseeMobilePhoneNumber && !!errors.lesseeMobilePhoneNumber} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyermobilephonenumber")} value={formValues.lesseeMobilePhoneNumber} hasError={!formValues.lesseePhoneNumber && !formValues.lesseeMobilePhoneNumber && !!errors.lesseeMobilePhoneNumber} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyeremail")} value={formValues.lesseeEmail} hasError={!!errors.lesseeEmail} />
          <InfoRow label={t("admin.fleet.vehicle.contract.add.buyerauthorizedrepresentativename")} value={formValues.lesseeAuthorizedRepresentativeName} hasError={!!errors.lesseeAuthorizedRepresentativeName} />
        </div>
      </div>

      {formValues.lesseeContactId && (
        <ContactEditCard
          contactId={formValues.lesseeContactId}
          show={showEditModal}
          onSuccess={handleEditSuccess}
          onClose={handleCloseModal}
          isFullScreen={false}
        />
      )}
    </>
  );
};

export default memo(ContractBuyerCard);
